﻿import { useState } from 'react'
import { useBookDemo } from './BookDemoContext'
import './ROICalculator.css'
const STEPS = ['Team Size','Hiring','Training & Development','HR Support']
const INITIAL = { employees:5000, avgSalary:85000, annualHires:500, cph:4700, ttfWeeks:12, turnoverPct:18, trainingHours:40, trainingWaste:35, hrTeamSize:15, hrSalaryAvg:75000 }
function fmt(n: number) { return '$' + Math.round(n).toLocaleString() }
function calcROI(d: typeof INITIAL) {
  const hiringTimeSave = d.annualHires * (d.ttfWeeks - d.ttfWeeks * 0.3) * (d.avgSalary / 52) * 0.05
  const hiringCostSave = d.annualHires * d.cph * 0.55
  const turnoverSave = d.employees * (d.turnoverPct / 100) * 0.2 * d.avgSalary * 0.35
  const trainingSave = d.employees * d.trainingHours * (d.avgSalary / 2080) * (d.trainingWaste / 100) * 0.65
  const hrTimeSave = d.hrTeamSize * d.hrSalaryAvg * 0.35
  const hiringTotal = hiringTimeSave + hiringCostSave + turnoverSave
  const developTotal = turnoverSave * 0.8 + trainingSave
  const assessTotal = trainingSave * 0.6 + hiringCostSave * 0.3
  const assistTotal = hrTimeSave
  const total = hiringTotal + developTotal + assessTotal + assistTotal
  const annualCost = d.employees * 12
  const roi = ((total - annualCost) / annualCost * 100).toFixed(0)
  const payback = Math.ceil(annualCost / (total / 12))
  return { hiringTotal, developTotal, assessTotal, assistTotal, total, annualCost, roi, payback }
}
export default function ROICalculator() {
  const openBookDemo = useBookDemo()
  const [step, setStep] = useState(0)
  const [data, setData] = useState(INITIAL)
  const [showResults, setShowResults] = useState(false)
  const update = (k: string, v: string | number) => setData(d => ({ ...d, [k]: Number(v) || 0 }))
  const res = calcROI(data)
  return (
    <section id="roi" className="roi-section section">
      <div className="container">
        <p className="section-eyebrow">ROI CALCULATOR</p>
        <h2 className="section-title">Calculate Your ROI</h2>
        <p className="section-subtitle">See the financial impact PossoBuild can deliver for your organization.</p>
        <div className="roi-wrapper">
          {!showResults ? (
            <div className="roi-form-panel">
              <div className="roi-steps">
                {STEPS.map((s, i) => (
                  <div key={s} className={`roi-step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`} onClick={() => setStep(i)}>
                    <div className="roi-step-num">{i < step ? '✓' : i + 1}</div>
                    <span>{s}</span>
                  </div>
                ))}
              </div>
              <div className="roi-fields">
                {step === 0 && <>
                  <div className="roi-field"><label>Total Employees</label><input type="number" value={data.employees} onChange={e => update('employees', e.target.value)} /><span className="field-hint">Total headcount organization-wide</span></div>
                  <div className="roi-field"><label>Average Annual Salary ($)</label><input type="number" value={data.avgSalary} onChange={e => update('avgSalary', e.target.value)} /></div>
                </>}
                {step === 1 && <>
                  <div className="roi-field"><label>Annual Hires</label><input type="number" value={data.annualHires} onChange={e => update('annualHires', e.target.value)} /></div>
                  <div className="roi-field"><label>Cost Per Hire ($)</label><input type="number" value={data.cph} onChange={e => update('cph', e.target.value)} /></div>
                  <div className="roi-field"><label>Current Time-to-Fill (weeks)</label><input type="number" value={data.ttfWeeks} onChange={e => update('ttfWeeks', e.target.value)} /></div>
                  <div className="roi-field"><label>Annual Turnover Rate (%)</label><input type="number" value={data.turnoverPct} onChange={e => update('turnoverPct', e.target.value)} /></div>
                </>}
                {step === 2 && <>
                  <div className="roi-field"><label>Training Hours Per Employee / Year</label><input type="number" value={data.trainingHours} onChange={e => update('trainingHours', e.target.value)} /></div>
                  <div className="roi-field"><label>Estimated Training Waste (%)</label><input type="number" value={data.trainingWaste} onChange={e => update('trainingWaste', e.target.value)} /><span className="field-hint">How much training time is irrelevant or ineffective?</span></div>
                </>}
                {step === 3 && <>
                  <div className="roi-field"><label>HR Team Size</label><input type="number" value={data.hrTeamSize} onChange={e => update('hrTeamSize', e.target.value)} /></div>
                  <div className="roi-field"><label>Average HR Salary ($)</label><input type="number" value={data.hrSalaryAvg} onChange={e => update('hrSalaryAvg', e.target.value)} /></div>
                </>}
              </div>
              <div className="roi-nav">
                {step > 0 && <button className="btn-secondary" onClick={() => setStep(s => s - 1)}>← Back</button>}
                {step < 3
                  ? <button className="btn-primary" onClick={() => setStep(s => s + 1)}>Next →</button>
                  : <button className="btn-primary" onClick={() => setShowResults(true)}>Calculate My ROI →</button>
                }
              </div>
            </div>
          ) : (
            <div className="roi-results-panel">
              <div className="roi-total-banner">
                <div>
                  <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Estimated Annual Value</p>
                  <div style={{ fontSize: '3.5rem', fontWeight: 800, color: 'var(--accent-light)' }}>{fmt(res.total)}</div>
                </div>
                <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                  <div style={{ textAlign: 'center' }}><div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--accent-light)' }}>{res.roi}%</div><div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>ROI</div></div>
                  <div style={{ textAlign: 'center' }}><div style={{ fontSize: '2rem', fontWeight: 800, color: '#34d399' }}>{res.payback}mo</div><div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>Payback</div></div>
                </div>
              </div>
              <div className="roi-breakdown">
                {([['🎯 SmartHire', res.hiringTotal, '#c9a84c'], ['📈 SmartDevelop', res.developTotal, '#a78bfa'], ['✅ SmartAssess', res.assessTotal, '#22d3ee'], ['💬 SmartAssist', res.assistTotal, '#34d399']] as [string, number, string][]).map(([label, val, color]) => (
                  <div key={label} className="roi-breakdown-row">
                    <span>{label}</span>
                    <div className="roi-bar-wrap"><div className="roi-bar" style={{ width: `${(val / res.total * 100).toFixed(1)}%`, background: color }}></div></div>
                    <span style={{ color, fontWeight: 700 }}>{fmt(val)}</span>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem', flexWrap: 'wrap' }}>
                <button className="btn-primary" onClick={() => openBookDemo('Book a Demo')}>Book a Demo to Validate →</button>
                <button className="btn-secondary" onClick={() => setShowResults(false)}>← Recalculate</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
